/// IRPCS Annex IV — distress signals.
///
/// Every COLREGS-compliant mariner is required to know these on
/// sight (or sound). The list is non-flag: NC hoist appears in
/// Annex IV but it lives in `@nav-games/flags`; everything here is
/// pyrotechnic, audio, radio, or visual-but-not-flag.
///
/// Categories let the Reference page group them — at sea you tend
/// to think "what can I make right now" rather than alphabetical.

export type DistressCategory =
  | "pyrotechnic"
  | "audio"
  | "radio"
  | "visual"
  | "electronic";

export function categoryLabel(c: DistressCategory): string {
  switch (c) {
    case "pyrotechnic":
      return "Pyrotechnic";
    case "audio":
      return "Audio";
    case "radio":
      return "Radio / voice";
    case "visual":
      return "Visual (non-pyro)";
    case "electronic":
      return "Electronic";
  }
}

export const DISTRESS_CATEGORIES: readonly DistressCategory[] = [
  "pyrotechnic",
  "audio",
  "radio",
  "visual",
  "electronic",
];

/// Priority level matters for VHF calls — Mayday (distress) requires
/// immediate assistance for grave + imminent danger to life or
/// vessel; Pan-Pan (urgency) is for serious situations that are NOT
/// immediately life-threatening. Defaults to "distress" so existing
/// Annex IV entries stay correct without modification.
export type SignalPriority = "distress" | "urgency" | "safety";

export interface DistressSignal {
  /// Stable answer key for flashcards.
  id: string;
  /// Short name a sailor would use — "Red parachute flare".
  name: string;
  /// One-line summary for buttons + short cards.
  shortMeaning: string;
  /// Annex IV verbatim or near-verbatim description.
  meaning: string;
  category: DistressCategory;
  /// When the signal is most appropriate — for the explainer panel.
  use: string;
  /// Defaults to "distress" if omitted. The Pan-Pan entry uses
  /// "urgency" so the Reference page can group / colour-code it
  /// distinctly from the Mayday family of signals.
  priority?: SignalPriority;
}

export const DISTRESS_SIGNALS: Readonly<Record<string, DistressSignal>> =
  Object.freeze({
    gun_at_minute: {
      id: "gun_at_minute",
      name: "Gun at intervals",
      shortMeaning: "Gun every minute",
      meaning:
        "A gun or other explosive signal fired at intervals of about a minute.",
      category: "audio",
      use: "Historic but still listed — any audible explosive cue at minute spacing.",
    },
    continuous_fog_horn: {
      id: "continuous_fog_horn",
      name: "Continuous fog horn",
      shortMeaning: "Continuous fog-horn sound",
      meaning:
        "A continuous sounding with any fog-signalling apparatus.",
      category: "audio",
      use: "Distinguishes distress from the timed Rule 35 fog signals.",
    },
    red_stars: {
      id: "red_stars",
      name: "Red star rockets",
      shortMeaning: "Rockets firing red stars",
      meaning:
        "Rockets or shells, throwing red stars fired one at a time at short intervals.",
      category: "pyrotechnic",
      use: "Long-range night-time visual; carry several to repeat the call.",
    },
    sos_morse: {
      id: "sos_morse",
      name: "SOS in Morse",
      shortMeaning: "··· ––– ··· (SOS)",
      meaning:
        "A signal made by radiotelegraphy or any other signalling method consisting of the group ··· ––– ··· (SOS) in the Morse Code.",
      category: "radio",
      use: "Aldis lamp, whistle, torch flashes — any medium that can carry Morse.",
    },
    mayday: {
      id: "mayday",
      name: "Mayday call",
      shortMeaning: "Spoken word 'Mayday' — DISTRESS",
      meaning:
        "A signal sent by radiotelephony consisting of the spoken word \"Mayday\". Reserved for grave and imminent danger to a vessel or person, requiring immediate assistance.",
      category: "radio",
      use: "VHF Ch 16 — preferred over DSC alone in international waters. Use ONLY when life or vessel is in immediate danger.",
      priority: "distress",
    },
    panpan: {
      id: "panpan",
      name: "Pan-Pan call",
      shortMeaning: "Spoken word 'Pan-Pan' — URGENCY",
      meaning:
        "A signal sent by radiotelephony consisting of the spoken words \"Pan-Pan\" repeated three times. Indicates a very urgent message concerning the safety of a vessel or a person — but NOT one in grave and imminent danger.",
      category: "radio",
      use: "VHF Ch 16. Use when you need help but are not in immediate danger — e.g. disabled engine in safe water, man overboard recovered but injured, medical advice request, vessel partially aground at HW. ALWAYS use Pan-Pan rather than Mayday if the situation does not yet threaten life or imminent loss of the vessel.",
      priority: "urgency",
    },
    nc_flags: {
      id: "nc_flags",
      name: "NC flags hoist",
      shortMeaning: "Flag N over flag C",
      meaning:
        "The International Code Signal of distress indicated by N.C.",
      category: "visual",
      use: "Daytime visual; mast or rigging hoist visible to nearby vessels.",
    },
    square_and_ball: {
      id: "square_and_ball",
      name: "Square flag + ball",
      shortMeaning: "Square flag over (or under) a ball shape",
      meaning:
        "A signal consisting of a square flag having above or below it a ball or anything resembling a ball.",
      category: "visual",
      use: "Improvised — bucket below a flag works; daytime, close range.",
    },
    flames: {
      id: "flames",
      name: "Flames on the vessel",
      shortMeaning: "Flames on board (tar/oil)",
      meaning:
        "Flames on the vessel (as from a burning tar barrel, oil barrel, etc.).",
      category: "visual",
      use: "Last-resort signal — historically the burning-barrel method.",
    },
    red_parachute: {
      id: "red_parachute",
      name: "Red parachute flare",
      shortMeaning: "Red parachute flare or red hand flare",
      meaning:
        "A rocket parachute flare or a hand flare showing a red light.",
      category: "pyrotechnic",
      use: "Parachute = long-range alert; hand flare = pinpointing for a rescuer in sight.",
    },
    orange_smoke: {
      id: "orange_smoke",
      name: "Orange smoke",
      shortMeaning: "Orange-coloured smoke signal",
      meaning: "A smoke signal giving off orange-coloured smoke.",
      category: "pyrotechnic",
      use: "Daytime — better than flares in bright sun for SAR aircraft.",
    },
    arms_signal: {
      id: "arms_signal",
      name: "Outstretched-arms wave",
      shortMeaning: "Arms outstretched, slowly raised and lowered",
      meaning:
        "Slowly and repeatedly raising and lowering arms outstretched to each side.",
      category: "visual",
      use: "Person in water or on a casualty; the universal in-sight gesture.",
    },
    epirb: {
      id: "epirb",
      name: "EPIRB",
      shortMeaning: "Emergency Position-Indicating Radio Beacon",
      meaning:
        "Signals transmitted by emergency position-indicating radio beacons (EPIRBs).",
      category: "electronic",
      use: "406 MHz to Cospas-Sarsat satellites — works anywhere with sky view.",
    },
    dsc_alert: {
      id: "dsc_alert",
      name: "DSC distress alert",
      shortMeaning: "VHF / MF Digital Selective Calling distress",
      meaning:
        "Approved signals transmitted by radiocommunications systems — including DSC distress alerts on VHF Ch 70 and MF 2187.5 kHz.",
      category: "electronic",
      use: "Press the red button — sends MMSI + position to all DSC-equipped stations.",
    },
    sart: {
      id: "sart",
      name: "SART response",
      shortMeaning: "Search-and-Rescue radar transponder return",
      meaning:
        "Approved signals transmitted by radiocommunications systems — including SARTs displaying as a line of 12 dots on a rescuer's radar.",
      category: "electronic",
      use: "Activated in a liferaft — shows up on any 9 GHz radar within ~5 nm.",
    },
    orange_canvas: {
      id: "orange_canvas",
      name: "Orange canvas + square/circle",
      shortMeaning: "Orange canvas with black square and circle",
      meaning:
        "A piece of orange-coloured canvas with either a black square and circle, or other appropriate symbol (for identification from the air).",
      category: "visual",
      use: "Helicopter / fixed-wing SAR visual reference — packs flat in a grab-bag.",
    },
    dye_marker: {
      id: "dye_marker",
      name: "Dye marker",
      shortMeaning: "Sea-dye marker",
      meaning:
        "A dye marker, releasing fluorescent (typically green) dye into the water.",
      category: "visual",
      use: "Marks your position for an aircraft once they're searching nearby.",
    },
  });

export const DISTRESS_IDS: readonly string[] = Object.keys(DISTRESS_SIGNALS);
